import React, { useState, useRef, useEffect } from "react";
import { Phone, X, Search, User, Delete } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTwilio } from "@/contexts/TwilioContext";
import { useDialerSearch } from "@/hooks/useDialerSearch";
import { useCallHistory } from "@/hooks/useCallHistory";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { CallHistoryItem } from "./CallHistoryItem";

interface MobileDialerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileDialer({ isOpen, onClose }: MobileDialerProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+55");
  const [searchQuery, setSearchQuery] = useState("");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState("dial");
  const [myTwilioNumber, setMyTwilioNumber] = useState<string | null>(null);
  const dialerRef = useRef<HTMLDivElement>(null);
  
  const { makeCall, isOnCall } = useTwilio();
  const { results, loading } = useDialerSearch(searchQuery);
  const { callHistory, loading: historyLoading } = useCallHistory();
  const { user } = useAuth();

  // Fetch user's Twilio number
  useEffect(() => {
    const fetchMyTwilioNumber = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('app_users')
          .select('twilio_phone_number')
          .eq('auth_user_id', user.id)
          .single();
        
        if (error) {
          console.error('Error fetching Twilio number:', error);
          return;
        }
        
        setMyTwilioNumber(data?.twilio_phone_number || null);
      } catch (err) {
        console.error('Error:', err);
      }
    };
    
    fetchMyTwilioNumber();
  }, [user?.id]);

  // Center dialer on open
  useEffect(() => {
    if (isOpen && dialerRef.current) {
      const rect = dialerRef.current.getBoundingClientRect();
      setPosition({
        x: (window.innerWidth - rect.width) / 2,
        y: (window.innerHeight - rect.height) / 2,
      });
    }
  }, [isOpen]);

  // Keyboard input handler
  useEffect(() => {
    if (!isOpen || activeTab !== "dial") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent if user is typing in a search field
      if ((e.target as HTMLElement).tagName === "INPUT") return;

      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        setPhoneNumber((prev) => prev + e.key);
      } else if (e.key === "*" || e.key === "#" || e.key === "+") {
        e.preventDefault();
        setPhoneNumber((prev) => prev + e.key);
      } else if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        setPhoneNumber((prev) => prev.slice(0, -1));
      } else if (e.key === "Enter" && phoneNumber) {
        e.preventDefault();
        handleCall();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, activeTab, phoneNumber]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".dialer-header")) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dialerRef.current) {
        const rect = dialerRef.current.getBoundingClientRect();
        
        // Calculate new position
        let newX = e.clientX - dragStart.x;
        let newY = e.clientY - dragStart.y;
        
        // Constrain to viewport bounds
        const minX = 0;
        const minY = 0;
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;
        
        // Apply constraints
        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));
        
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handleNumberClick = (digit: string) => {
    setPhoneNumber((prev) => prev + digit);
  };

  const handleDelete = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  };

  const handleCall = async () => {
    if (phoneNumber.trim()) {
      // If number starts with +, use it directly, otherwise prepend country code
      const fullNumber = phoneNumber.startsWith("+") 
        ? phoneNumber.trim() 
        : countryCode + phoneNumber.trim();
      await makeCall(fullNumber);
      onClose();
    }
  };

  const handleContactCall = async (contact: any) => {
    if (contact.phone) {
      // Extract country code and number if contact has it
      let contactNumber = contact.phone;
      if (contactNumber.startsWith("+1")) {
        setCountryCode("+1");
        setPhoneNumber(contactNumber.substring(2));
      } else if (contactNumber.startsWith("+55")) {
        setCountryCode("+55");
        setPhoneNumber(contactNumber.substring(3));
      } else {
        setPhoneNumber(contactNumber);
      }
      
      await makeCall(contact.phone, contact.type === 'contact' ? contact.id : undefined, contact.type === 'lead' ? contact.id : undefined);
      setActiveTab("dial");
      onClose();
    }
  };

  if (!isOpen) return null;

  const dialPadButtons = [
    { digit: "1", letters: "" },
    { digit: "2", letters: "ABC" },
    { digit: "3", letters: "DEF" },
    { digit: "4", letters: "GHI" },
    { digit: "5", letters: "JKL" },
    { digit: "6", letters: "MNO" },
    { digit: "7", letters: "PQRS" },
    { digit: "8", letters: "TUV" },
    { digit: "9", letters: "WXYZ" },
    { digit: "*", letters: "" },
    { digit: "0", letters: "+" },
    { digit: "#", letters: "" },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div
        ref={dialerRef}
        className={cn(
          "fixed z-50 bg-background rounded-lg shadow-2xl overflow-hidden",
          "w-[400px] max-w-[95vw]",
          isDragging ? "cursor-grabbing" : "cursor-default"
        )}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div className="dialer-header cursor-grab active:cursor-grabbing bg-primary text-primary-foreground p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            <span className="font-semibold">Discador</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full rounded-none border-b">
            <TabsTrigger value="dial" className="flex-1">Discar</TabsTrigger>
            <TabsTrigger value="contacts" className="flex-1">Contatos</TabsTrigger>
            <TabsTrigger value="recents" className="flex-1">Recentes</TabsTrigger>
          </TabsList>

          {/* Dial Tab */}
          <TabsContent value="dial" className="p-6 space-y-6">
            {/* Display */}
            <div className="space-y-3">
              {/* My Twilio Number */}
              {myTwilioNumber && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                  <Phone className="h-3.5 w-3.5" />
                  <span className="font-medium">My Number:</span>
                  <span className="font-mono">{myTwilioNumber}</span>
                </div>
              )}
              
              {/* Country Code Selector */}
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="+1">🇺🇸 +1 (USA)</SelectItem>
                  <SelectItem value="+55">🇧🇷 +55 (Brasil)</SelectItem>
                </SelectContent>
              </Select>

              {/* Phone Number Display */}
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
                placeholder="Digite ou cole o número"
                className="text-3xl font-mono tracking-wider text-center h-[60px] bg-muted border-none focus-visible:ring-2"
                disabled={isOnCall}
              />
            </div>

            {/* Dial Pad */}
            <div className="grid grid-cols-3 gap-4">
              {dialPadButtons.map((button) => (
                <Button
                  key={button.digit}
                  variant="outline"
                  className="h-16 flex flex-col items-center justify-center text-xl font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => handleNumberClick(button.digit)}
                  disabled={isOnCall}
                >
                  <span>{button.digit}</span>
                  {button.letters && (
                    <span className="text-xs text-muted-foreground">
                      {button.letters}
                    </span>
                  )}
                </Button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="destructive"
                size="lg"
                onClick={handleDelete}
                disabled={!phoneNumber || isOnCall}
                className="h-14"
              >
                <Delete className="h-5 w-5 mr-2" />
                Apagar
              </Button>
              <Button
                variant="default"
                size="lg"
                onClick={handleCall}
                disabled={!phoneNumber || isOnCall}
                className="h-14 bg-green-600 hover:bg-green-700 text-white"
              >
                <Phone className="h-5 w-5 mr-2" />
                Ligar
              </Button>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="p-6 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar contato ou lead..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Results */}
            <ScrollArea className="h-[400px]">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Searching...
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? "No results found" : "Type to search"}
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map((contact) => (
                    <div
                      key={`${contact.type}-${contact.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => handleContactCall(contact)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {contact.name?.charAt(0) || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{contact.name}</p>
                          <Badge variant="secondary" className="text-xs">
                            {contact.type === 'contact' ? 'Contato' : 'Lead'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {contact.phone}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Recents Tab */}
          <TabsContent value="recents" className="h-[500px] overflow-hidden">
            <ScrollArea className="h-full">
              {historyLoading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Carregando histórico...</p>
                </div>
              ) : callHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                  <Phone className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nenhuma chamada recente</p>
                </div>
              ) : (
                <div className="p-2">
                  {callHistory.map((call) => (
                    <CallHistoryItem
                      key={call.id}
                      call={call}
                      onCallBack={(phoneNumber) => {
                        setPhoneNumber(phoneNumber.replace(countryCode, ''));
                        setActiveTab('dial');
                      }}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
